import React, { useState } from 'react';

import styled from '@emotion/styled';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Table, Button, Text } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { HRS_CONFIG } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { Hrs__RoleDefault, useHrsRoleDefaultsQuery } from '@admin/schema';

import { Role } from '../../users/role';
import { UpsertModal } from './upsert_modal';

const RoleLabels = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
`;

export const HRSRoleDefaultsList: React.FC = () => {
  useTitle('HRS Role Defaults');
  const [showUpsertModal, setShowUpsertModal] = useState<boolean>(false);
  const [editingRoleDefault, setEditingRoleDefault] = useState<Hrs__RoleDefault | undefined>(undefined);
  const canView = usePermission(HRS_CONFIG.resource, HRS_CONFIG.actions.read);
  const canUpdate = usePermission(HRS_CONFIG.resource, HRS_CONFIG.actions.update);

  const { data, loading, refetch } = useHrsRoleDefaultsQuery({
    client,
    skip: !canView,
  });

  return (
    <Permission
      resource={HRS_CONFIG.resource}
      action={HRS_CONFIG.actions.read}
      denied={
        <Panel>
          <Panel.Body>
            <Text tag="p" style="muted">
              You do not have permission to view HRS configuration.
            </Text>
          </Panel.Body>
        </Panel>
      }
    >
      <Panel>
        <Panel.Body>
          <Table responsive striped>
            <thead>
              <tr>
                <th className="col-sm-2">Job Code & Title</th>
                <th className="col-sm-2">Roles</th>
                {canUpdate && (
                  <th className="col-sm-1 text-right">
                    <Button
                      type="button"
                      kind="mint"
                      onClick={() => {
                        setEditingRoleDefault(undefined);
                        setShowUpsertModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add
                    </Button>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {data?.hrsRoleDefaults.map((roleDefault) => (
                <tr key={roleDefault.id}>
                  <td className="col-sm-2">{roleDefault.businessTitle}</td>
                  <td className="col-sm-2">
                    <RoleLabels>
                      {roleDefault.roles.map((role, index) => (
                        <Role key={index} role={role} />
                      ))}
                    </RoleLabels>
                  </td>
                  {canUpdate && (
                    <td className="col-sm-1 text-right">
                      <Button
                        kind="primary"
                        onClick={() => {
                          setEditingRoleDefault(roleDefault);
                          setShowUpsertModal(true);
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          {loading && <Spinner />}
        </Panel.Body>
      </Panel>
      {showUpsertModal && canUpdate && (
        <UpsertModal
          currentBusinessTitles={data?.hrsBusinessTitles ?? []}
          roleDefault={editingRoleDefault}
          closeModal={() => {
            setEditingRoleDefault(undefined);
            setShowUpsertModal(false);
            refetch();
          }}
        />
      )}
    </Permission>
  );
};
